import { Head, router } from '@inertiajs/react'
import { ChevronDown, ChevronUp, List, Map as MapIcon } from 'lucide-react'
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import Breadcrumb from '@/components/public/Breadcrumb'
import { CreateYourPageCard } from '@/components/public/CreateYourPage'
import ExploreCtaBanner from '@/components/public/ExploreCtaBanner'
import CategorySections, {
  type CategorySectionData,
} from '@/components/public/CategorySection'
import {
  EXPLORE_NAME,
  EXPLORE_PATH,
  PUBLIC_BAND,
  PUBLIC_CONTAINER,
} from '@/components/public/publicShell'
import CitySelect, {
  ALL_CITIES,
  NEARBY_CITY,
} from '@/components/public/CitySelect'
import ServiceFilters from '@/components/public/ServiceFilters'
import type { BusinessCategory } from '@/components/public/CreateYourPage'
import BewaveFooter from '@/components/public/BewaveFooter'
import PublicHeader from '@/components/public/PublicHeader'
import SchoolCard from '@/components/public/SchoolCard'
import SurfShorts from '@/components/public/SurfShorts'
import {
  hasCoordinates,
  type CategoryFacet,
  type CityOption,
  type DirectorySchool,
  type MapBounds,
  type ServiceFacet,
} from '@/types/publicSchool'

// lazy() aqui NÃO é por SSR (que está desligado). É porque o resolver do Inertia
// usa import.meta.glob(..., { eager: true }): todas as páginas vivem num bundle
// só, então um import estático de leaflet entraria no chunk que TODA página
// autenticada baixa. Ver app/frontend/entrypoints/inertia.tsx.
const SchoolsMap = lazy(() => import('@/components/public/SchoolsMap'))

/** Onde a lista começa. É o alvo do scroll ao filtrar — ver `rolarParaResultados`. */
const RESULTADOS_ID = 'resultados'

/**
 * As três alturas da folha de resultados no mobile.
 *
 * `recolhida` é a novidade: antes a folha só ia de 45dvh a tela cheia, e quem
 * quisesse ver o mapa inteiro não tinha para onde mandá-la — ela cobria metade
 * da tela o tempo todo. Recolhida sobra só a barra, como o carrinho flutuante de
 * uma loja: continua dizendo quantos negócios há e volta com um toque.
 *
 * `media` é o padrão porque abre a tela mostrando as duas coisas — os primeiros
 * cards e o mapa em volta deles.
 */
type SheetState = 'recolhida' | 'media' | 'cheia'

const SHEET_HEIGHT: Record<SheetState, string> = {
  // Só a barra. Altura fixa em rem (e não em dvh) porque o conteúdo dela é
  // sempre o mesmo — alça, contador e botão — em qualquer tamanho de tela.
  // 4.5rem é a soma medida desse conteúdo: em 4rem o botão ficava cortado pelo
  // `overflow-hidden` de quem está recolhida.
  recolhida: 'h-[4.5rem]',
  media: 'h-[45dvh]',
  cheia: 'h-[calc(100dvh-var(--public-header-h,7.5rem))]',
}

/** Sobe ou desce um degrau, sem passar do fim em nenhuma das duas pontas. */
const SHEET_ORDER: SheetState[] = ['recolhida', 'media', 'cheia']

function nextSheetState(
  current: SheetState,
  direction: 'up' | 'down',
): SheetState {
  const index = SHEET_ORDER.indexOf(current) + (direction === 'up' ? 1 : -1)
  return SHEET_ORDER[Math.min(Math.max(index, 0), SHEET_ORDER.length - 1)]
}

interface ExploreProps {
  schools: DirectorySchool[]
  cities: CityOption[]
  /** Facetas das 7 categorias. Hoje só o `?category=` da vitrine as usa — a
   *  fila da página filtra por serviço. */
  categories: CategoryFacet[]
  /** Os 32 serviços do catálogo — o filtro principal da página. */
  services: ServiceFacet[]
  /** Vista atual: mapa (lista + mapa lado a lado) ou lista (seções por categoria). */
  view: 'mapa' | 'lista'
  /** As seções da vista lista. Vazio na vista mapa — o servidor nem monta. */
  category_sections: CategorySectionData[]
  query: string | null
  selected_city: string | null
  selected_category: BusinessCategory | null
  selected_service: string | null
  bounds: MapBounds | null
  origin: { lat: number; lng: number } | null
  /** Em que cidade o visitante está — só quando o mapa recorta em volta dele. */
  origin_city: CityOption | null
  total_count: number
  truncated: boolean
}

export default function Explore({
  schools,
  cities,
  services,
  view,
  category_sections,
  query,
  selected_city,
  selected_category,
  selected_service,
  bounds,
  origin,
  origin_city,
  total_count,
  truncated,
}: ExploreProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [sheetState, setSheetState] = useState<SheetState>('media')
  const [searchOnMove, setSearchOnMove] = useState(true)

  // Guarda a última área enviada para não repetir requisição quando o Leaflet
  // dispara moveend por reflow (resize, invalidateSize) sem o usuário mexer.
  const lastBounds = useRef<string | null>(
    bounds ? JSON.stringify(bounds) : null,
  )

  const located = useMemo(() => schools.filter(hasCoordinates), [schools])

  // O rótulo do serviço filtrado ("Pousadas"). Sai das facetas, que já vêm do
  // servidor com o nome — o cliente não tem catálogo de serviço para consultar.
  const serviceLabel = useMemo(
    () =>
      services.find((service) => service.key === selected_service)?.label ??
      null,
    [services, selected_service],
  )

  // Assinatura dos filtros que NÃO são geográficos. O mapa usa isto para separar
  // "a lista mudou porque filtrei" (reenquadra) de "a lista mudou porque arrastei
  // o mapa" (não reenquadra, senão vira laço) — ver FitToSchools.
  const filterKey = useMemo(
    () => JSON.stringify([query, selected_city, selected_service]),
    [query, selected_city, selected_service],
  )
  const cityLabel = cities.find((city) => city.slug === selected_city)?.label

  const visit = useCallback(
    (params: Record<string, string | number | undefined>) => {
      const query: Record<string, string | number> = {}
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') query[key] = value
      })

      router.get(EXPLORE_PATH, query, {
        preserveState: true,
        preserveScroll: true,
      })
    },
    [],
  )

  const currentQuery = useMemo(
    () => ({
      q: query ?? undefined,
      city: selected_city ?? undefined,
      lat: origin?.lat,
      lng: origin?.lng,
      category: selected_category ?? undefined,
      service: selected_service ?? undefined,
      // A lista é o padrão: só o mapa precisa aparecer na URL.
      view: view === 'mapa' ? 'mapa' : undefined,
    }),
    [query, selected_city, selected_category, selected_service, view, origin],
  )

  /**
   * Leva a tela para o começo dos resultados.
   *
   * Filtrar da barra fixa trocava a lista lá embaixo enquanto a pessoa continuava
   * olhando o hero — parecia que o clique não tinha feito nada. Só na vista
   * lista: no mapa não há hero na frente de nada.
   *
   * Desconta a altura do header (a variável que ele publica), senão o título da
   * primeira seção nasce por baixo dele.
   */
  const rolarParaResultados = useCallback(() => {
    if (view !== 'lista') return

    const alvo = document.getElementById(RESULTADOS_ID)
    if (!alvo) return

    const header =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--public-header-h',
        ),
      ) || 64

    window.scrollTo({
      top: Math.max(
        alvo.getBoundingClientRect().top + window.scrollY - header,
        0,
      ),
      behavior: 'smooth',
    })
  }, [view])

  // Trocar de cidade abandona a área do mapa: o resultado passa a ser "onde quer
  // que essas escolas estejam", e o mapa reenquadra em cima delas.
  const handleCity = useCallback(
    (city: string) => {
      lastBounds.current = null
      // "Perto de você" é a ausência de parâmetro — é ele que devolve o recorte
      // de 120 km em volta do visitante. Vale como opção do select justamente
      // para dar caminho de VOLTA depois de escolher uma cidade.
      if (city === NEARBY_CITY) {
        visit({ ...currentQuery, city: undefined })
        rolarParaResultados()
        return
      }
      // "Todas as cidades" vira `?city=todas`, e não parâmetro nenhum: no mapa,
      // ausência de cidade significa "perto de mim" (recorte de 120 km), então
      // limpar o filtro sem deixar rastro devolvia o recorte em vez de mostrar
      // tudo, que é o oposto do que a pessoa pediu.
      visit({ ...currentQuery, city: city === ALL_CITIES ? 'todas' : city })
      rolarParaResultados()
    },
    [currentQuery, visit, rolarParaResultados],
  )

  const handleView = useCallback(
    (next: 'mapa' | 'lista') => {
      // Abandona a área do mapa: na vista lista não há "Limpar área", e o
      // usuário ficaria com um recorte geográfico invisível e sem saída.
      lastBounds.current = null
      router.get(
        EXPLORE_PATH,
        {
          ...Object.fromEntries(
            Object.entries({ ...currentQuery, view: undefined }).filter(
              ([, value]) => value !== undefined && value !== '',
            ),
          ),
          ...(next === 'mapa' ? { view: 'mapa' } : {}),
        },
        // `preserveScroll: false` de propósito, ao contrário dos filtros: a
        // vista lista é muito mais alta, e manter o scroll cairia no meio de
        // uma seção qualquer.
        { preserveState: true, preserveScroll: false },
      )
    },
    [currentQuery],
  )

  const handleService = useCallback(
    (service: string | null) => {
      lastBounds.current = null
      visit({ ...currentQuery, service: service ?? undefined })
      rolarParaResultados()
    },
    [currentQuery, visit, rolarParaResultados],
  )

  const handleBoundsChange = useCallback(
    (next: MapBounds) => {
      const serialized = JSON.stringify(next)
      if (serialized === lastBounds.current) return
      lastBounds.current = serialized

      visit({
        ...currentQuery,
        ne_lat: next.ne_lat,
        ne_lng: next.ne_lng,
        sw_lat: next.sw_lat,
        sw_lng: next.sw_lng,
      })
    },
    [currentQuery, visit],
  )

  // Pede a localização só quando o visitante chega sem nenhum recorte. Com filtro
  // na URL (link compartilhado, voltar do navegador) o enquadramento já foi
  // escolhido, e roubar isso seria pior do que não perguntar.
  //
  // Se ele negar, nada acontece: o mapa segue enquadrando todas as escolas. Não há
  // segunda tentativa — insistir em prompt de permissão é hostil.
  const askedForLocation = useRef(false)

  useEffect(() => {
    if (askedForLocation.current) return
    if (origin || bounds || selected_city || query || selected_service) return
    if (!('geolocation' in navigator)) return

    askedForLocation.current = true
    navigator.geolocation.getCurrentPosition(
      (position) => {
        visit({
          lat: position.coords.latitude.toFixed(5),
          lng: position.coords.longitude.toFixed(5),
        })
      },
      () => {
        // Negado ou indisponível: segue com a visão geral.
      },
      { timeout: 8000, maximumAge: 5 * 60 * 1000 },
    )
    // Só na chegada; as dependências são lidas uma vez de propósito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasAnyFilter =
    bounds !== null ||
    selected_city !== null ||
    selected_category !== null ||
    selected_service !== null ||
    query !== null

  /**
   * O mapa fica montado enquanto houver área ativa, mesmo sem nenhuma escola no
   * resultado. Antes ele era substituído pelo aviso de "sem localização" e o
   * usuário ficava preso: a lista zerava e não sobrava nada para arrastar de volta.
   */
  const showMap = located.length > 0 || bounds !== null

  const clearArea = useCallback(() => {
    lastBounds.current = null
    visit(currentQuery)
  }, [currentQuery, visit])

  const clearAll = useCallback(() => {
    lastBounds.current = null
    visit({})
  }, [visit])

  // Arrastar a alça: para cima sobe um degrau, para baixo desce um.
  // Deliberadamente sem seguir o dedo em tempo real — animar a altura a cada
  // pointermove causa reflow do mapa embaixo a cada quadro.
  const gripStart = useRef<number | null>(null)

  const handleGripDown = useCallback((event: React.PointerEvent) => {
    // Toque nos botões da barra NÃO é gesto da folha.
    //
    // Sem esta linha o toque no botão borbulhava até aqui e a barra o tratava
    // como toque curto: os dois setSheetState entravam na fila e se anulavam
    // (recolhida → media pelo gesto, media → recolhida pelo clique). Medido com
    // toque real: partindo da folha recolhida, o botão não fazia nada. O
    // `setPointerCapture` abaixo ainda piorava, redirecionando o ponteiro do
    // botão para a barra.
    if ((event.target as HTMLElement).closest('button')) return

    gripStart.current = event.clientY
    event.currentTarget.setPointerCapture(event.pointerId)
  }, [])

  const handleGripUp = useCallback((event: React.PointerEvent) => {
    if (gripStart.current === null) return

    const delta = event.clientY - gripStart.current
    gripStart.current = null

    // Menos de 8px é toque, não arrasto — e toque na barra sobe a folha um
    // degrau, que é o gesto óbvio de quem quer mais lista à vista.
    if (Math.abs(delta) < 8)
      setSheetState((state) => nextSheetState(state, 'up'))
    else
      setSheetState((state) => nextSheetState(state, delta < 0 ? 'up' : 'down'))
  }, [])

  // Clicar num pino precisa mostrar o card: com a folha recolhida ele fica fora
  // de vista, então ela sobe até a meia altura — e não até o topo, que cobriria
  // justamente o mapa de onde veio o clique.
  // Tudo o que a barra de filtros precisa, num objeto só: ela é montada em dois
  // lugares (no header, na vista mapa; dentro do card, na lista) e repetir dez
  // props nos dois é como os dois divergem na primeira mudança.
  const filtros = {
    cities,
    selectedCity: selected_city,
    originCity: origin_city,
    onCity: handleCity,
    services,
    selectedService: selected_service,
    onService: handleService,
    view,
    onView: handleView,
  }

  const handleSelectFromMap = useCallback((id: string) => {
    setSheetState((state) => (state === 'recolhida' ? 'media' : state))
    setActiveId(id)
    // Espera a folha terminar de subir, senão o scroll mira uma altura que ainda
    // vai mudar.
    setTimeout(() => {
      document
        .getElementById(`school-${id}`)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }, 320)
  }, [])

  return (
    <>
      <Head
        title={
          cityLabel
            ? `Escolas de surf em ${cityLabel} — ${EXPLORE_NAME}`
            : `Escolas de surf — ${EXPLORE_NAME}`
        }
      />

      <div
        className={cn(
          'bg-bewave-surface text-foreground',
          // O travamento em 100dvh existe SÓ por causa da folha arrastável do
          // mobile: sem ele, arrastar a folha arrastava a página junto. Na vista
          // lista não há folha, e manter o travamento prenderia a página de
          // seções em uma tela de altura — ela simplesmente não rolaria.
          view === 'mapa'
            ? 'h-[100dvh] overflow-hidden lg:h-auto lg:min-h-screen lg:overflow-visible'
            : 'min-h-screen',
        )}
      >
        <PublicHeader
          brandHref="/"
          // Os filtros são da PÁGINA: barra própria, fixa no topo, sempre à mão
          // nas duas vistas. Chegaram a morar dentro do card do chat do Rafa
          // por um momento — o resultado é que ele virava obrigatório até para
          // clicar num chip, e a busca por conversa era um caminho a mais,
          // nunca um pedágio. Vale de novo se o chat voltar.
          belowRow={<BarraDeFiltros {...filtros} />}
        />

        {view === 'lista' ? (
          <div className="pb-4">
            {/* O chat do Rafa saía aqui, flutuando no canto. Está DESLIGADO: o
                componente (RafaChat.tsx), o controller e o service continuam no
                repo, mas nada os monta e a rota POST /explore/rafa não existe
                mais — sem ela o endpoint não fica público gastando crédito da
                API enquanto a busca por conversa está fora do ar. Para religar:
                devolver a rota, o estado da conversa e este render. */}
            <div id={RESULTADOS_ID}>
              {category_sections.length > 0 ? (
                // Sem container aqui: cada seção decide a própria largura — o texto
                // dentro da faixa, o trilho sangrando até a borda.
                <>
                  <CategorySections
                    sections={category_sections}
                    listings={schools}
                    // O "Ver todos" de cada seção leva o recorte junto: sem isto,
                    // quem filtrou Fortaleza caía no Brasil inteiro.
                    filters={{
                      q: query,
                      city: selected_city,
                      service: selected_service,
                    }}
                  />
                  {/* Um convite só, no fim — antes ele fechava CADA trilho e
                    aparecia quatro vezes na mesma tela. */}
                  <div className={cn(PUBLIC_CONTAINER, 'pb-4 pt-6')}>
                    <ExploreCtaBanner />
                  </div>
                </>
              ) : (
                // Nenhum negócio casa com o recorte: em vez de sete títulos com
                // carrossel vazio, o convite — que é o que sobra para oferecer.
                <div className={cn(PUBLIC_CONTAINER, 'py-16')}>
                  <p className="text-base text-gray-600">
                    Nada encontrado com esses filtros.
                  </p>
                  <CreateYourPageCard
                    category={selected_category ?? undefined}
                    className="mt-8"
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={cn(PUBLIC_BAND, 'lg:flex lg:items-start')}>
            {/* Mesma <section> nos dois layouts, trocando só as classes: duplicar a
              lista para desktop e mobile repetiria os id={`school-${id}`} e
              quebraria o scrollIntoView do clique no pino. */}
            <section
              className={cn(
                // Mesmo gutter do header no lado esquerdo, senão o breadcrumb, o
                // título e os cards não alinham com a logo e a busca.
                'px-5 sm:px-6 lg:pl-10 lg:pr-6',
                // Mobile: folha fixa sobre o mapa, arrastável pela alça. Ela é o
                // fundo cinza da página, não uma superfície branca: os cards dentro
                // é que são brancos, e com a folha branca eles sumiriam.
                'fixed inset-x-0 bottom-0 z-30 overscroll-contain rounded-t-2xl bg-bewave-surface pb-6 shadow-[0_-4px_24px_rgba(0,0,0,0.12)] transition-[height] duration-300',
                SHEET_HEIGHT[sheetState],
                // Recolhida a folha NÃO rola: com 4rem de altura, um resto de
                // scroll deixaria a barra deslizar para fora da vista e sumir com
                // o único jeito de trazer a lista de volta.
                sheetState === 'recolhida'
                  ? 'overflow-hidden'
                  : 'overflow-y-auto',
                // Desktop: coluna normal, sem nada de folha.
                'lg:static lg:z-auto lg:h-auto lg:overflow-visible lg:rounded-none lg:py-6 lg:shadow-none lg:transition-none lg:w-[58%]',
              )}
            >
              {/* A barra da folha, só no mobile: alça de arrasto, contador e o par
                de setas.

                As setas existem porque a alça sozinha não se anuncia — quem não
                tenta arrastar não descobre que a folha se move. E são DUAS, uma
                por sentido, e não um botão que alterna: com três estados, um
                botão só tem que adivinhar para onde ir, e o caminho de descer
                acabava pulando o meio ou sumindo. Cada seta anda exatamente um
                degrau, e a desabilitada diz em qual das três pontas você está. */}
              <div
                data-alca
                onPointerDown={handleGripDown}
                onPointerUp={handleGripUp}
                // Opaca de propósito, para os cards rolarem por baixo dela — por isso
                // acompanha a cor da folha em vez de ficar transparente.
                className="sticky top-0 z-10 -mx-5 cursor-grab touch-none bg-bewave-surface px-5 pb-2 pt-3 sm:-mx-6 sm:px-6 lg:hidden"
              >
                <span className="mx-auto block h-1.5 w-10 rounded-full bg-gray-300" />

                <div className="mt-2 flex items-center justify-between gap-3">
                  {/* O contador é o <h1> da tela no mobile — o título de baixo, que
                    diz a mesma coisa, fica só no desktop. Repetir os dois seria
                    o mesmo texto duas vezes em quatro polegadas. */}
                  <h1 className="text-sm font-semibold text-gray-900">
                    {schools.length === 0
                      ? 'Nada encontrado'
                      : `${schools.length} ${resultLabel(schools.length, serviceLabel)}`}
                    {cityLabel && ` em ${cityLabel}`}
                  </h1>

                  <div className="flex shrink-0 items-center gap-1.5">
                    <SheetStep
                      direction="down"
                      state={sheetState}
                      onStep={setSheetState}
                    />
                    <SheetStep
                      direction="up"
                      state={sheetState}
                      onStep={setSheetState}
                    />
                  </div>
                </div>
              </div>

              {/* Só no desktop: dentro da folha ela caía DEPOIS do contador, e
                uma trilha de navegação abaixo do título da tela lê como se a
                página tivesse começado no meio. O header do mobile já leva para
                Início e para Explorar. */}
              <div className="hidden lg:block">
                <Breadcrumb
                  items={[
                    { label: 'Início', href: '/' },
                    { label: 'Explorar' },
                  ]}
                />
              </div>

              {/* "Negócio", e não "escola": o diretório lista escola de surf,
                pousada, shaper, fotógrafo — chamar tudo de escola era o rótulo
                da primeira versão, quando só havia escolas. Com um serviço
                filtrado o próprio rótulo dele é mais específico e entra no
                lugar ("4 Pousadas"). */}
              <h1 className="mt-2 hidden text-xl font-semibold lg:block">
                {schools.length === 0
                  ? 'Nada encontrado'
                  : `${schools.length} ${resultLabel(schools.length, serviceLabel)}`}
                {cityLabel && ` em ${cityLabel}`}
              </h1>

              {/* O teto do servidor corta a resposta; dizer o total evita a impressão
                de que só existem essas. */}
              {truncated && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Mostrando as {schools.length} mais próximas de {total_count}.
                  Aproxime o mapa ou use os filtros para refinar.
                </p>
              )}

              {origin && !truncated && schools.length > 0 && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Ordenadas pela distância até você.
                </p>
              )}

              {schools.length === 0 ? (
                <div className="mt-4 space-y-3">
                  <p className="text-muted-foreground">
                    {bounds
                      ? 'Nenhum negócio nesta área do mapa.'
                      : 'Nenhum negócio bate com os filtros.'}
                  </p>
                  {/* Sem uma saída explícita o usuário fica preso: uma área vazia
                    zera a lista, e antes disto o mapa sumia junto com ela. */}
                  {hasAnyFilter && (
                    <Button type="button" variant="outline" onClick={clearAll}>
                      Ver tudo
                    </Button>
                  )}
                </div>
              ) : (
                <div
                  id="lista-negocios"
                  className="mt-4 grid gap-x-4 gap-y-6 sm:grid-cols-2 xl:grid-cols-3"
                >
                  {schools.map((school) => (
                    <SchoolCard
                      key={school.id}
                      school={school}
                      active={activeId === school.id}
                      hovered={hoveredId === school.id}
                      onHover={setHoveredId}
                      onSelect={setActiveId}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Mapa sticky em vez de painel com scroll próprio — evita scroll-chaining
              e o comportamento errático de trackpad entre dois overflows. */}
            <section
              aria-label="Mapa dos negócios"
              // `isolate` é o que segura a guerra de z-index do Leaflet: ele usa até
              // 1000 nos próprios panes e controles, e sem um contexto de empilhamento
              // próprio esses valores competiam com a página inteira — foi o que me
              // levou a inventar z-1100/1200 no header e na folha, e foi por isso que
              // o dialog (z-50) abria por baixo de tudo.
              className="relative isolate h-[calc(100dvh-var(--public-header-h,7.5rem))] lg:sticky lg:top-[var(--public-header-h,7.5rem)] lg:w-[42%]"
            >
              {!showMap ? (
                <div className="flex h-full items-center justify-center border-l bg-white p-6 text-center text-sm text-muted-foreground">
                  Nenhum negócio {cityLabel ? `em ${cityLabel} ` : ''}tem
                  localização cadastrada ainda.
                </div>
              ) : (
                <>
                  <Suspense
                    fallback={
                      <Skeleton className="h-full w-full rounded-none" />
                    }
                  >
                    <SchoolsMap
                      schools={located}
                      hoveredId={hoveredId}
                      activeId={activeId}
                      onSelect={handleSelectFromMap}
                      visible
                      searchOnMove={searchOnMove}
                      filterKey={filterKey}
                      onBoundsChange={handleBoundsChange}
                      viewBounds={bounds}
                    />
                  </Suspense>

                  <div className="absolute left-1/2 top-3 z-[1000] flex -translate-x-1/2 items-center gap-2 rounded-full border bg-white/95 px-3 py-2 shadow">
                    <Checkbox
                      id="search-on-move"
                      checked={searchOnMove}
                      onCheckedChange={(checked) => {
                        const next = checked === true
                        setSearchOnMove(next)
                        // Desligar volta ao resultado sem recorte de área; ligar só passa
                        // a valer no próximo movimento do mapa.
                        if (!next && bounds) {
                          lastBounds.current = null
                          visit(currentQuery)
                        }
                      }}
                    />
                    <Label
                      htmlFor="search-on-move"
                      className="cursor-pointer text-xs font-normal"
                    >
                      Buscar ao mover o mapa
                    </Label>
                  </div>

                  {bounds && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={clearArea}
                      // Acima da folha recolhida no mobile: em `bottom-6` ele
                      // ficava atrás da barra flutuante da lista.
                      className="absolute bottom-24 left-1/2 z-[1000] -translate-x-1/2 rounded-full shadow-lg lg:bottom-6"
                    >
                      Limpar área
                    </Button>
                  )}
                </>
              )}
            </section>
          </div>
        )}

        {/* Fim da página, abaixo do mapa: as páginas de criador por categoria,
            com o convite para criar a sua como mais um card de cada fileira.

            Enquanto não houver página nenhuma publicada, a vitrine não tem o que
            mostrar e cai no convite avulso — que é o mesmo conteúdo, só sem as
            fileiras. Sem esse fallback, o fim do diretório ficaria vazio hoje.

            Só no desktop, pelo mesmo motivo do rodapé: no mobile a folha da
            lista cobre a tela e nada abaixo do mapa é alcançável. */}
        {/* Só na vista mapa: na lista, cada seção já termina com o card de
            "coloque seu negócio aqui", e repetir o convite aqui embaixo seria a
            terceira vez que a mesma página pede a mesma coisa.

            Escondido no mobile pelo mesmo motivo do rodapé: ali a folha da lista
            cobre a tela e nada abaixo do mapa é alcançável. */}
        {view === 'mapa' && (
          <div
            className={cn(
              PUBLIC_BAND,
              'hidden px-5 py-10 sm:px-6 lg:block lg:px-10',
            )}
          >
            <ExploreCtaBanner />
          </div>
        )}

        {/* Os shorts do canal, logo acima do rodapé — a mesma seção da landing.
            Aqui ela fecha o diretório com gente de verdade na praia: quem rolou
            até o fim viu cards e filtros, e sai vendo aula acontecendo.

            Escondida no mobile da vista mapa pelo mesmo motivo do rodapé: ali a
            folha da lista cobre a tela e nada abaixo do mapa é alcançável. */}
        <div className={view === 'mapa' ? 'hidden lg:block' : undefined}>
          <SurfShorts variant="explore" />
        </div>

        <BewaveFooter
          cities={cities}
          // Na vista mapa o rodapé some no mobile: a folha da lista cobre a tela
          // e ele só apareceria depois de rolar o mapa inteiro. Na vista lista
          // não há folha, e a página rola até ele normalmente.
          className={view === 'mapa' ? 'hidden lg:block' : undefined}
        />
      </div>
    </>
  )
}

/**
 * A barra de filtros: os serviços, a cidade e a escolha entre mapa e lista.
 *
 * Um lugar só — o `belowRow` do header —, e o mesmo nas duas vistas. Já morou
 * dentro do card do chat do Rafa na vista lista; ter o mesmo controle em dois
 * lugares diferentes conforme a vista é como se perde o caminho de volta.
 */
function BarraDeFiltros({
  cities,
  selectedCity,
  originCity,
  onCity,
  services,
  selectedService,
  onService,
  view,
  onView,
}: {
  cities: CityOption[]
  selectedCity: string | null
  originCity: CityOption | null
  onCity: (city: string) => void
  services: ServiceFacet[]
  selectedService: string | null
  onService: (service: string | null) => void
  view: 'mapa' | 'lista'
  onView: (next: 'mapa' | 'lista') => void
}) {
  return (
    // No mobile os controles ficam numa linha e a fila de serviços na sua:
    // dividindo a linha com os dois botões sobravam uns 150px para 33 chips.
    <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
      <div className="flex shrink-0 items-center gap-2 lg:order-2">
        {/* A cidade é o ÚNICO filtro além do serviço. Os selos (Wi-Fi, aula para
            iniciante) saíram: descrevem uma escola de surf e não dizem nada
            sobre pousada, shaper ou fotógrafo — num diretório de nichos
            diferentes, viravam um filtro que zerava a lista sem explicar por
            quê. */}
        <CitySelect
          cities={cities}
          selected={selectedCity}
          nearbyCity={originCity}
          onChange={onCity}
          className="shrink-0 rounded-full border bg-white shadow-sm"
        />
        <ViewToggle view={view} onChange={onView} />
      </div>

      <ServiceFilters
        className="lg:order-1"
        facets={services}
        selected={selectedService}
        onChange={onService}
      />
    </div>
  )
}

/**
 * Uma seta da barra da folha: anda um degrau no seu sentido.
 *
 * Fica desabilitada quando não há degrau para onde ir — é assim que o par
 * reflete os três estados sem precisar de rótulo: no mapa cheio só a de subir
 * acende, na lista cheia só a de descer, e no meio as duas.
 *
 * Os rótulos falam do RESULTADO ("Ver mais do mapa", "Ver mais da lista") e não
 * do movimento da folha: para quem usa leitor de tela, "subir a folha" não diz o
 * que muda na tela.
 */
function SheetStep({
  direction,
  state,
  onStep,
}: {
  direction: 'up' | 'down'
  state: SheetState
  onStep: (update: (current: SheetState) => SheetState) => void
}) {
  const Icon = direction === 'up' ? ChevronUp : ChevronDown
  const disabled = nextSheetState(state, direction) === state

  return (
    <button
      type="button"
      disabled={disabled}
      aria-controls="lista-negocios"
      aria-label={direction === 'up' ? 'Ver mais da lista' : 'Ver mais do mapa'}
      onClick={() => onStep((current) => nextSheetState(current, direction))}
      // `touch-auto` desfaz o `touch-none` da barra; o gesto de arrasto ignora
      // toque em botão (ver handleGripDown), então os dois não brigam mais.
      className={cn(
        'inline-flex size-9 shrink-0 touch-auto items-center justify-center rounded-full border bg-white shadow-sm transition-colors',
        disabled
          ? 'border-gray-200 text-gray-300'
          : 'border-gray-300 text-gray-700 active:bg-gray-100',
      )}
    >
      <Icon className="size-4" aria-hidden="true" />
    </button>
  )
}

/**
 * Mapa ou lista.
 *
 * Vive na linha de filtros do header (o `belowRow`), e não flutuando sobre o
 * mapa: na vista lista não há mapa sobre o que flutuar, e dois lugares
 * diferentes para o mesmo controle é como se perde o caminho de volta.
 *
 * Renderizado nas duas vistas, sempre — se sumisse numa delas, o header mudaria
 * de altura ao trocar, e essa altura entra no `calc()` que dimensiona o mapa e a
 * folha da lista (`--public-header-h`).
 */
function ViewToggle({
  view,
  onChange,
}: {
  view: 'mapa' | 'lista'
  onChange: (next: 'mapa' | 'lista') => void
}) {
  const option = (active: boolean) =>
    cn(
      'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors',
      active
        ? 'bg-white text-gray-900 shadow-sm'
        : 'text-gray-500 hover:text-gray-700',
    )

  return (
    <div
      role="radiogroup"
      aria-label="Como ver o diretório"
      className="flex shrink-0 items-center gap-1 rounded-full bg-gray-100 p-1"
    >
      <button
        type="button"
        role="radio"
        aria-checked={view === 'mapa'}
        onClick={() => onChange('mapa')}
        className={option(view === 'mapa')}
      >
        <MapIcon className="size-4" aria-hidden="true" />
        {/* Rótulo só do sm para cima: na linha estreita do mobile o ícone
            sozinho já diferencia, e o texto empurraria a fila de filtros. */}
        <span className="hidden sm:inline">Mapa</span>
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={view === 'lista'}
        onClick={() => onChange('lista')}
        className={option(view === 'lista')}
      >
        <List className="size-4" aria-hidden="true" />
        <span className="hidden sm:inline">Lista</span>
      </button>
    </div>
  )
}

/**
 * O substantivo do contador de resultados.
 *
 * Com um serviço filtrado, o rótulo dele é mais informativo que "negócios" —
 * "4 Pousadas" diz o que "4 negócios" não diz. Sem filtro, cai no genérico,
 * porque a lista mistura escola de surf, pousada, shaper e fotógrafo.
 *
 * O rótulo do catálogo vem no plural ("Pousadas", "Fotógrafos") menos nas
 * aulas, que são singulares por construção ("Aula de Surf"); daí o caso de um
 * resultado só continuar usando o rótulo como veio.
 */
function resultLabel(count: number, serviceLabel: string | null): string {
  if (serviceLabel) return serviceLabel
  return count === 1 ? 'negócio' : 'negócios'
}
